"use client";

/**
 * Componente invisible que resetea el viewport (pinch-zoom o auto-zoom iOS)
 * en 3 casos:
 *   1. Cambio de ruta (navegación Next.js).
 *   2. Focusout de un input + el usuario toca fuera → reset diferido.
 *   3. Cambio de orientación (puede dejar el viewport roto).
 *
 * Se monta una sola vez en el layout raíz. No renderiza nada.
 */
import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { resetViewport, isZoomed } from "@/lib/viewport-reset";

export default function ViewportReset() {
  const pathname = usePathname();

  // 1. Reset al cambiar de ruta
  useEffect(() => {
    // Pequeño delay para dejar que la ruta se hidrate antes del reset.
    const t = setTimeout(() => {
      if (isZoomed()) resetViewport();
    }, 150);
    return () => clearTimeout(t);
  }, [pathname]);

  // 2. Reset después de focusout + tap fuera (usuario terminó de escribir)
  useEffect(() => {
    let pendingReset = false;

    const onFocusOut = (e: FocusEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const tag = target.tagName;
      if (tag !== "INPUT" && tag !== "SELECT" && tag !== "TEXTAREA") return;
      pendingReset = true;
      // Si otro input recibe focus inmediatamente, cancelamos.
      setTimeout(() => {
        if (!pendingReset) return;
        const active = document.activeElement;
        const activeTag = active?.tagName;
        const stillInInput =
          activeTag === "INPUT" || activeTag === "SELECT" || activeTag === "TEXTAREA";
        pendingReset = false;
        if (!stillInInput && isZoomed()) resetViewport();
      }, 300);
    };

    document.addEventListener("focusout", onFocusOut, true);
    return () => document.removeEventListener("focusout", onFocusOut, true);
  }, []);

  // 3. Reset al rotar dispositivo
  useEffect(() => {
    const onOrientation = () => {
      setTimeout(() => {
        if (isZoomed()) resetViewport();
      }, 300);
    };
    window.addEventListener("orientationchange", onOrientation);
    return () => window.removeEventListener("orientationchange", onOrientation);
  }, []);

  return null;
}
